import { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useMeQuery, useOnlineUsersQuery } from '@shared/api';
import { useRealtimeState } from '@shared/realtime/useRealtime';
import { realtime } from '@shared/realtime/ws';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Администратор',
  director: 'Генеральный директор',
  manager: 'Руководитель',
  pm_specialist: 'Руководитель проекта',
  pm: 'Менеджер проекта',
  lawyer: 'Юрист',
  dispatcher_operator: 'Диспетчер',
  estimator: 'Сметчик',
  estimator_plus: 'Сметчик+',
  chief_estimator: 'Начальник СЦО',
  economist: 'Экономист',
  economist_plus: 'Экономист+',
  chief_economist: 'Главный экономист',
  financier: 'Финансист',
  pto_engineer: 'Инженер ПТО',
  engineer: 'Инженер',
  engineer_plus: 'Инженер+',
  foreman: 'Прораб',
  site_chief: 'Начальник участка',
  field_specialist_plus: 'Прораб+',
  ksp_engineer: 'Инженер КСП',
  mto_specialist: 'МТО',
  mto_specialist_plus: 'МТО+',
  supplier: 'Снабжение',
  logist: 'Логист',
  surveyor: 'Геодезист',
  meeting_organizer: 'Совещания',
  hr: 'Кадры',
  hr_admin: 'Кадры (админ)',
  hr_auditor: 'Кадры (аудитор)',
};

const pulse = keyframes`
  0%, 100% { transform: scale(1);   opacity: 0.85; }
  50%      { transform: scale(1.8); opacity: 0; }
`;

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

interface DotProps {
  $state: 'open' | 'connecting' | 'reconnecting' | 'closed' | 'idle';
}

const DotInner = styled.span<DotProps>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme, $state }) =>
    $state === 'open'
      ? theme.colors.status.success
      : $state === 'connecting' || $state === 'reconnecting'
        ? theme.colors.status.warning
        : theme.colors.text.muted};
  position: relative;
  z-index: 1;
`;

const DotRipple = styled.span<DotProps>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: ${({ theme, $state }) =>
    $state === 'open' ? theme.colors.status.success : theme.colors.status.warning};
  ${({ $state }) =>
    ($state === 'open' || $state === 'reconnecting') &&
    css`
      animation: ${pulse} 1.6s ease-out infinite;
    `}
`;

const DotWrap = styled.span`
  position: relative;
  width: 8px;
  height: 8px;
  display: inline-flex;
`;

const Trigger = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 160ms ease;
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${({ theme }) => `${theme.colors.status.success}22`};
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 11px;
  font-weight: 700;
`;

const Popup = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 340px;
  max-height: 420px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme }) => theme.shadow.md};
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PopupHeader = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeaderSub = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const List = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const Empty = styled.div`
  padding: 24px 14px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

const UserRow = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  gap: 10px;

  &:last-child {
    border-bottom: none;
  }
`;

const UserAvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
`;

const OnlineDot = styled.span`
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success};
  border: 2px solid ${({ theme }) => theme.colors.backgroundElevated};
`;

const UserBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserNameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelfTag = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`;

const UserMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function stateLabel(s: ReturnType<typeof useRealtimeState>): string {
  switch (s) {
    case 'open':
      return 'Онлайн';
    case 'connecting':
      return 'Подключение…';
    case 'reconnecting':
      return 'Переподключение…';
    case 'closed':
      return 'Офлайн';
    default:
      return 'Онлайн';
  }
}

function formatUserSub(department: string | null, roles: string[], email: string): string {
  const roleLabel = roles.map((r) => ROLE_LABELS[r] ?? r).filter(Boolean).join(', ');
  if (department && roleLabel) return `${department} · ${roleLabel}`;
  if (department) return department;
  if (roleLabel) return roleLabel;
  return email;
}

export function RealtimeBadge() {
  const state = useRealtimeState();
  const { data: me } = useMeQuery();
  const { data: onlineData, refetch } = useOnlineUsersQuery(undefined, {
    pollingInterval: 30000,
  });
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return realtime.subscribe((e) => {
      if (e.event === 'presence_update' || e.event === 'connected') {
        void refetch();
      }
    });
  }, [refetch]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const users = onlineData?.users ?? [];
  const onlineCount = onlineData?.count ?? (me?.user ? 1 : 0);

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) void refetch();
        }}
        title="Пользователи онлайн — нажмите, чтобы посмотреть список"
        aria-expanded={open}
      >
        <DotWrap>
          <DotRipple $state={state} />
          <DotInner $state={state} />
        </DotWrap>
        <span>{stateLabel(state)}</span>
        <CountBadge>{onlineCount}</CountBadge>
      </Trigger>

      {open && (
        <Popup role="dialog" aria-label="Пользователи онлайн">
          <PopupHeader>
            <span>Сейчас онлайн ({onlineCount})</span>
            <HeaderSub>{state === 'open' ? 'в сети' : stateLabel(state)}</HeaderSub>
          </PopupHeader>
          <List>
            {users.length === 0 ? (
              <Empty>Загрузка списка пользователей…</Empty>
            ) : (
              users.map((u) => {
                const isSelf = me?.user?.id === u.id;
                return (
                  <UserRow key={u.id}>
                    <UserAvatarWrap>
                      <UserAvatar>{initials(u.full_name || u.email)}</UserAvatar>
                      <OnlineDot />
                    </UserAvatarWrap>
                    <UserBody>
                      <UserNameLine>
                        <span>{u.full_name || u.email}</span>
                        {isSelf && <SelfTag>Вы</SelfTag>}
                      </UserNameLine>
                      <UserMeta>{formatUserSub(u.department, u.roles, u.email)}</UserMeta>
                    </UserBody>
                  </UserRow>
                );
              })
            )}
          </List>
        </Popup>
      )}
    </Wrap>
  );
}
